export interface VisitTimingData {
  createdAt?: string; assignedAt?: string; scheduledFor?: string; dueBy?: string;
  startedAt?: string; endedAt?: string; cancelledAt?: string; occurredAt?: string; outcomeRecordedAt?: string;
  actualDurationMinutes?: number;
  outcome?: { recordedAt?: string; label?: string };
  history?: Array<{ id: string; type: string; at: string }>;
  notes?: Array<{ id: string; addedAt: string }>;
  activities?: Array<{ id: string; type: string; at: string; label?: string }>;
  photos?: Array<{ capturedAt: string; phase: string }>;
}
export const validVisitTime = (value?: string): value is string => !!value && Number.isFinite(new Date(value).getTime());

export function visitPrimaryTime(v: VisitTimingData): string | undefined {
  return [v.occurredAt, v.endedAt, v.cancelledAt, v.startedAt, v.scheduledFor, v.createdAt].find(validVisitTime);
}

export function visitDurationSeconds(v: VisitTimingData): number | undefined {
  if (validVisitTime(v.startedAt) && validVisitTime(v.endedAt)) {
    const seconds = (new Date(v.endedAt).getTime() - new Date(v.startedAt).getTime()) / 1000;
    return seconds >= 0 ? seconds : undefined;
  }
  return typeof v.actualDurationMinutes === 'number' && Number.isFinite(v.actualDurationMinutes) && v.actualDurationMinutes >= 0 ? v.actualDurationMinutes * 60 : undefined;
}

/** Never substitute updatedAt or the current time for missing action times. */
export function visitTimeline(v: VisitTimingData): Array<{ id: string; type: string; at: string; label?: string }> {
  const entries: Array<{ id: string; type: string; at: string; label?: string }> = [];
  const add = (type: string, at?: string, label?: string, id = `${type}:${at}`) => {
    if (validVisitTime(at)) entries.push({ id, type, at, label });
  };
  add('created', v.createdAt); add('assigned', v.assignedAt); add('started', v.startedAt);
  add('completed', v.endedAt); add('cancelled', v.cancelledAt);
  add('outcome', v.outcome?.recordedAt || v.outcomeRecordedAt, v.outcome?.label);
  for (const event of v.history || []) add(event.type, event.at, undefined, event.id);
  for (const note of v.notes || []) add('note', note.addedAt, undefined, note.id);
  for (const activity of v.activities || []) add(activity.type, activity.at, activity.label, activity.id);
  (v.photos || []).forEach((photo, index) => add(`photo_${photo.phase}`, photo.capturedAt, undefined, `photo:${index}`));
  const seen = new Set<string>();
  return entries.filter((e) => {
    // Lifecycle fields mirror history; independent actions may share a timestamp.
    const key = ['assigned', 'cancelled', 'created', 'started', 'completed', 'outcome'].includes(e.type)
      ? `${e.type}:${new Date(e.at).getTime()}` : `${e.type}:${e.id}`;
    if (seen.has(key)) return false;
    seen.add(key); return true;
  }).sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
}

export interface VisitMetrics {
  total: number; completed: number; inProgress: number; missed: number; cancelled: number; overdue: number;
  completionRate: number | null; successRate: number | null; onTimeRate: number | null;
  timedStarts: number; lateStarts: number; outcomesRecorded: number;
  averageDurationSeconds: number | null; totalDurationSeconds: number;
  averageStartDelaySeconds: number | null; averageAssignmentWaitSeconds: number | null;
}
